"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectCheckoutFormSubmitActions = exports.selectShippingOptions = exports.sortByShippingPrice = exports.selectCartHasValidShippingMethod = exports.selectCartLoading = exports.selectCustomerGroupKey = exports.selectCartDoesNotHavePromoCode = exports.selectCartShippingAddress = exports.selectCartHasPromoCode = exports.selectCartLineItems = exports.selectParsedCart = void 0;
const verifyImmutableRecord_1 = __importDefault(require("@simplisafe/ewok/monet-utils/verifyImmutableRecord"));
const fromEmpty_1 = require("../utils/fromEmpty");
const safeFind_1 = require("../utils/safeFind");
const safeProp_1 = require("../utils/safeProp");
const chainProp_1 = require("../utils/chainProp");
const monet_1 = require("monet");
const always_1 = __importDefault(require("ramda/src/always"));
const equals_1 = __importDefault(require("ramda/src/equals"));
const F_1 = __importDefault(require("ramda/src/F"));
const ifElse_1 = __importDefault(require("ramda/src/ifElse"));
const pathEq_1 = __importDefault(require("ramda/src/pathEq"));
const propOr_1 = __importDefault(require("ramda/src/propOr"));
const T_1 = __importDefault(require("ramda/src/T"));
const actions_1 = require("../cart/actions");
const cart_1 = require("../commercetools/cart");
const price_1 = require("../commercetools/price");
const select_1 = require("../redux/select");
const lenses_1 = require("./lenses");
/**
 * Selects the cart from state,
 * converts it to a Maybe<Cart> instead of RemoteData<Cart>,
 * and then verifies that any Maybe/Either keys have the prototype methods intact.
 */
const selectParsedCart = (state) => (0, select_1.selectCart)(state)
    .toMaybe()
    .map(verifyImmutableRecord_1.default);
exports.selectParsedCart = selectParsedCart;
/**
 * returns Maybe<LineItem[]>.
 *
 * If the array is empty returns None.
 */
const selectCartLineItems = (state) => (0, exports.selectParsedCart)(state)
    .chain((0, safeProp_1.safeProp)('lineItems'))
    .chain(fromEmpty_1.fromEmpty);
exports.selectCartLineItems = selectCartLineItems;
/**
 * Returns true if the cart contains a matching promo code.
 */
const selectCartHasPromoCode = (promoCode) => (state) => (0, exports.selectParsedCart)(state)
    .chain((0, chainProp_1.chainProp)('discountCodes'))
    .chain((0, safeFind_1.safeFind)((0, pathEq_1.default)(['discountCode', 'obj', 'code'], promoCode)))
    .cata(F_1.default, T_1.default);
exports.selectCartHasPromoCode = selectCartHasPromoCode;
/**
 * Returns the fields inside the shipping address
 */
const selectCartShippingAddress = (state) => (0, exports.selectParsedCart)(state)
    .chain((0, chainProp_1.chainProp)('shippingAddress'));
exports.selectCartShippingAddress = selectCartShippingAddress;
/**
 * Takes a promo code and returns Just that code if the cart does not contain it,
 * otherwise it returns None.
 *
 * Useful if you want to do something with a code that is not currently applied to the cart.
 */
const selectCartDoesNotHavePromoCode = (promoCode) => (state) => promoCode.chain(code => (0, exports.selectParsedCart)(state)
    .chain((0, chainProp_1.chainProp)('discountCodes'))
    .chain((0, safeFind_1.safeFind)((0, pathEq_1.default)(['discountCode', 'obj', 'code'], code)))
    .cata(() => promoCode, () => (0, monet_1.None)()));
exports.selectCartDoesNotHavePromoCode = selectCartDoesNotHavePromoCode;
const selectCustomerGroupKey = (state) => (0, exports.selectParsedCart)(state)
    .chain((0, chainProp_1.chainProp)('customerGroup'))
    .chain(customerGroup => monet_1.Maybe.fromNull(customerGroup?.obj?.key));
exports.selectCustomerGroupKey = selectCustomerGroupKey;
/** Check if the cart is loading */
const selectCartLoading = (state) => state.get('cart').loading;
exports.selectCartLoading = selectCartLoading;
/** Check if the cart shipping method is valid */
const selectCartHasValidShippingMethod = (state) => {
    const cart = lenses_1.cartValueLens.get(state);
    return cart
        .chain((0, chainProp_1.chainProp)('shippingInfo'))
        .chain((0, safeProp_1.safeProp)('shippingMethodState'))
        // MatchesCart comes from commercetools and means the cart has a valid shipping method set
        .chain(val => val !== 'MatchesCart' ? (0, monet_1.None)() : (0, monet_1.Just)(val))
        .cata(F_1.default, T_1.default);
};
exports.selectCartHasValidShippingMethod = selectCartHasValidShippingMethod;
// used to sort formatted shipping options from selectShippingOptions in asending order by price.
// removes the currency symbol from the string and parses the string into a float for subtraction.
exports.sortByShippingPrice = ((a, b) => {
    const firstLocalizedPrice = (0, propOr_1.default)('', 'localizedShippingPrice', a);
    const secondLocalizedPrice = (0, propOr_1.default)('', 'localizedShippingPrice', b);
    return (parseFloat(firstLocalizedPrice.substring(1)) - parseFloat(secondLocalizedPrice.substring(1)));
});
//takes in the shipping response and cart. Formats the data and returns a maybe array of shipping options sorted by price
const selectShippingOptions = (results) => (state) => {
    const cart = (0, select_1.selectCart)(state);
    const locale = (0, select_1.selectLocale)(state);
    const shippingOptions = cart.map((_cart) => {
        const formattedResults = results.map((option) => {
            const id = (0, propOr_1.default)('', 'id', option);
            const key = (0, propOr_1.default)('', 'key', option);
            const name = (0, propOr_1.default)('', 'name', option);
            const shippingRate = (0, actions_1.getMatchingShippingRate)(option);
            const shippingPrice = (0, actions_1.getMatchingShippingPrice)(shippingRate, _cart).getOrElse(0);
            const localizedShippingPrice = (0, price_1.localizedDisplayPrice)(locale)(shippingPrice).getOrElse('');
            const localizedShippingOptionName = name + ': ' + localizedShippingPrice;
            return {
                description: option.description,
                id: id,
                key: key,
                localizedShippingName: localizedShippingOptionName,
                localizedShippingPrice: localizedShippingPrice,
                name: name
            };
        });
        // eslint-disable-next-line
        return formattedResults.sort(exports.sortByShippingPrice);
    });
    return shippingOptions.toMaybe();
};
exports.selectShippingOptions = selectShippingOptions;
// returns all of the actions required for the current form's state
const selectCheckoutFormSubmitActions = (values) => (state) => {
    const locale = (0, select_1.selectLocale)(state);
    const usShippingAddress = {
        additionalStreetInfo: (0, propOr_1.default)('', 'additionalStreetInfo', values),
        city: (0, propOr_1.default)('', 'city', values),
        country: (0, propOr_1.default)('', 'country', values),
        email: (0, propOr_1.default)('', 'email', values),
        firstName: (0, propOr_1.default)('', 'firstName', values),
        lastName: (0, propOr_1.default)('', 'lastName', values),
        phone: (0, propOr_1.default)('', 'phone', values),
        postalCode: (0, propOr_1.default)('', 'postalCode', values),
        state: (0, propOr_1.default)('', 'state', values),
        streetName: (0, propOr_1.default)('', 'streetName', values)
    };
    const ukShippingAddress = {
        additionalStreetInfo: (0, propOr_1.default)('', 'additionalStreetInfo', values),
        city: (0, propOr_1.default)('', 'city', values),
        country: (0, propOr_1.default)('', 'country', values),
        email: (0, propOr_1.default)('', 'email', values),
        firstName: (0, propOr_1.default)('', 'firstName', values),
        lastName: (0, propOr_1.default)('', 'lastName', values),
        phone: (0, propOr_1.default)('', 'phone', values),
        postalCode: (0, propOr_1.default)('', 'postalCode', values),
        streetName: (0, propOr_1.default)('', 'streetName', values)
    };
    const localeShippingAddress = (0, ifElse_1.default)((0, equals_1.default)('en-US'), (0, always_1.default)(usShippingAddress), (0, always_1.default)(ukShippingAddress))(locale);
    const leadSource = (0, propOr_1.default)('', 'foundInfoThrough', values);
    const additionalLeadSource = (0, propOr_1.default)('', 'additionalFoundInfoThrough', values);
    const sameBillingAddress = (0, propOr_1.default)(false, 'sameAddress', values);
    const shippingOption = (0, propOr_1.default)('', 'shippingOption', values);
    const hasAddress = !!localeShippingAddress;
    const isSameBillingAddress = hasAddress && sameBillingAddress === true;
    const actions = [
        (0, cart_1.buildShippingAddressUpdateAction)(localeShippingAddress),
        isSameBillingAddress
            ? (0, cart_1.buildBillingAddressUpdateAction)(localeShippingAddress)
            : undefined,
        {
            action: 'setShippingMethod',
            shippingMethod: {
                id: shippingOption,
                typeId: 'shipping-method'
            }
        },
        (0, cart_1.buildCustomFieldUpdateAction)({
            name: 'leadOther',
            value: additionalLeadSource
        }),
        (0, cart_1.buildCustomFieldUpdateAction)({
            name: 'leadSource',
            value: leadSource
        })
    ];
    const filteredActions = actions.filter((action) => !!action);
    return monet_1.Maybe.fromUndefined(filteredActions);
};
exports.selectCheckoutFormSubmitActions = selectCheckoutFormSubmitActions;
//# sourceMappingURL=select.js.map